import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface StepProgress {
  id: string;
  user_id: string;
  step_id: number;
  status: 'not_started' | 'in_progress' | 'completed';
  started_at: string | null;
  completed_at: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

interface UseStepProgressReturn {
  progressData: StepProgress[];
  loading: boolean;
  error: string | null;
  updateStepStatus: (stepId: number, status: StepProgress['status'], memo?: string) => Promise<void>;
  getStepProgress: (stepId: number) => StepProgress | undefined;
  getCurrentStepIndex: () => number;
  getOverallProgress: () => number;
  refreshProgress: () => Promise<void>;
}

export function useStepProgress(): UseStepProgressReturn {
  const { user } = useAuth();
  const [progressData, setProgressData] = useState<StepProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!user) {
      setProgressData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('research_step_progress')
        .select('*')
        .eq('user_id', user.id)
        .order('step_id', { ascending: true });

      if (fetchError) throw fetchError;

      setProgressData(data || []);
    } catch (err) {
      console.error('Error fetching step progress:', err);
      setError(err instanceof Error ? err.message : '進捗データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const updateStepStatus = async (
    stepId: number,
    status: StepProgress['status'],
    memo?: string
  ) => {
    if (!user) {
      throw new Error('ログインが必要です');
    }

    try {
      const existingProgress = progressData.find((p) => p.step_id === stepId);
      const now = new Date().toISOString();

      const updateData: Partial<StepProgress> = {
        status,
        updated_at: now,
      };

      // ステータスに応じて日時を更新
      if (status === 'in_progress' && !existingProgress?.started_at) {
        updateData.started_at = now;
      }
      if (status === 'completed') {
        updateData.completed_at = now;
        if (!existingProgress?.started_at) {
          updateData.started_at = now;
        }
      }
      if (memo !== undefined) {
        updateData.memo = memo;
      }

      if (existingProgress) {
        // 既存データを更新
        const { error: updateError } = await supabase
          .from('research_step_progress')
          .update(updateData)
          .eq('id', existingProgress.id);

        if (updateError) throw updateError;
      } else {
        // 新規データを挿入
        const { error: insertError } = await supabase
          .from('research_step_progress')
          .insert({
            user_id: user.id,
            step_id: stepId,
            ...updateData,
          });

        if (insertError) throw insertError;
      }

      // データを再取得
      await fetchProgress();
    } catch (err) {
      console.error('Error updating step status:', err);
      throw err;
    }
  };

  const getStepProgress = (stepId: number): StepProgress | undefined => {
    return progressData.find((p) => p.step_id === stepId);
  };

  const getCurrentStepIndex = (): number => {
    // 最初の未完了ステップのインデックスを返す（0-based）
    const firstIncomplete = progressData.find(
      (p) => p.status !== 'completed'
    );
    
    if (firstIncomplete) {
      return firstIncomplete.step_id - 1;
    }

    // 全て完了している場合は最後のステップ
    if (progressData.length > 0 && progressData.every((p) => p.status === 'completed')) {
      return Math.max(...progressData.map((p) => p.step_id)) - 1;
    }

    // データがない場合は最初のステップ
    return 0;
  };

  const getOverallProgress = (): number => {
    if (progressData.length === 0) return 0;
    
    const completedCount = progressData.filter((p) => p.status === 'completed').length;
    const totalSteps = 8; // 全8ステップ
    
    return Math.round((completedCount / totalSteps) * 100);
  };

  const refreshProgress = async () => {
    await fetchProgress();
  };

  return {
    progressData,
    loading,
    error,
    updateStepStatus,
    getStepProgress,
    getCurrentStepIndex,
    getOverallProgress,
    refreshProgress,
  };
}
